import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { FiActivity, FiChevronLeft, FiChevronRight, FiRefreshCw, FiSearch } from 'react-icons/fi';
import Layout from '../components/Layout';
import AnalyticsDateRange from '../components/AnalyticsDateRange';
import { formatSingaporeDateTime } from '../utils/singaporeTime';
import { useAnalyticsChartRange, clipAnalyticsChartData, ANALYTICS_DATE_XAXIS_PROPS } from '../hooks/useAnalyticsChartRange';

const PAGE_SIZE = 10;
const CONTACT_PAGE_SIZE = 20;


const formatDeviceDisplay = (device) => {
    const d = (device || '').trim().toLowerCase();
    if (!d) return '—';
    if (d.includes('mobile') || d.includes('phone') || d.includes('tablet')) return '📲';
    if (d.includes('desktop')) return '💻';
    return device;
};

const StatCard = ({ label, visitors, views }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <div className="mt-2 flex gap-6">
            <div>
                <p className="text-2xl font-bold text-blue-700">{visitors ?? 0}</p>
                <p className="text-xs text-gray-500">Visitors</p>
            </div>
            <div>
                <p className="text-2xl font-bold text-green-700">{views ?? 0}</p>
                <p className="text-xs text-gray-500">Views</p>
            </div>
        </div>
    </div>
);

const TablePagination = ({ page, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 text-sm">
            <span className="text-gray-500">
                Page {page} of {totalPages}
            </span>
            <div className="flex gap-1">
                <button
                    type="button"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                    className={`p-1 rounded ${page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                    aria-label="Previous page"
                >
                    <FiChevronLeft />
                </button>
                <button
                    type="button"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`p-1 rounded ${page === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                    aria-label="Next page"
                >
                    <FiChevronRight />
                </button>
            </div>
        </div>
    );
};

const PaginatedDataTable = ({ title, columns, rows, emptyText = 'No data' }) => {
    const [page, setPage] = useState(1);
    const allRows = rows || [];
    const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));

    useEffect(() => {
        setPage(1);
    }, [rows]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginatedRows = allRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
            <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-800">{title}</h2>
            </div>
            <div className="overflow-x-auto flex-1">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap"
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {!paginatedRows.length ? (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-6 text-center text-gray-500 text-sm">
                                    {emptyText}
                                </td>
                            </tr>
                        ) : (
                            paginatedRows.map((row, i) => (
                                <tr key={row._id || row.visitorId || row.path || `${page}-${i}`} className="hover:bg-gray-50">
                                    {columns.map((col) => (
                                        <td key={col.key} className="px-3 py-2 text-sm text-gray-800">
                                            {col.render ? col.render(row) : row[col.key] ?? '—'}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
    );
};

const SLGAnalyticsPage = () => {
    const [dashboard, setDashboard] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [clickChart, setClickChart] = useState({ data: [], lines: [], filterSummary: '' });
    const [sendChart, setSendChart] = useState({ data: [], lines: [], filterSummary: '' });
    const [chartTab, setChartTab] = useState('traffic');
    const { chartFrom, setChartFrom, chartTo, setChartTo, rangeReady } = useAnalyticsChartRange();
    const [loading, setLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(true);
    const [clickChartLoading, setClickChartLoading] = useState(true);
    const [sendChartLoading, setSendChartLoading] = useState(true);
    const [error, setError] = useState('');
    const [contactClicks, setContactClicks] = useState([]);
    const [contactLoading, setContactLoading] = useState(true);
    const [contactError, setContactError] = useState('');
    const [contactMeta, setContactMeta] = useState(null);
    const [contactSearchInput, setContactSearchInput] = useState('');
    const [contactSearch, setContactSearch] = useState('');
    const [clickSearchInput, setClickSearchInput] = useState('');
    const [clickSearch, setClickSearch] = useState('');
    const [sendSearchInput, setSendSearchInput] = useState('');
    const [sendSearch, setSendSearch] = useState('');
    const [contactPage, setContactPage] = useState(1);
    const [allContactClicks, setAllContactClicks] = useState([]);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const applyContactRows = (rows, meta = null) => {
        const list = Array.isArray(rows) ? rows : [];
        setAllContactClicks(list);
        setContactMeta(meta);
        setContactError('');
    };

    const filterContactRows = (rows, search) => {
        const q = String(search || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((row) => {
            const name = String(row.godName || '').toLowerCase();
            const webID = String(row.webID || '').toLowerCase();
            return name.includes(q) || webID.includes(q);
        });
    };

    const fetchDashboard = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setLoading(true);
            setContactLoading(true);
            setError('');
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/slg-analytics/dashboard`,
                { headers: authHeaders(), params: { from: chartFrom, to: chartTo } }
            );
            if (res.data.success) {
                const payload = res.data.data || {};
                setDashboard(payload);
                if (Array.isArray(payload.contactClicks)) {
                    applyContactRows(payload.contactClicks, payload.contactClicksMeta || null);
                } else {
                    // Older API without contactClicks on dashboard — try dedicated route
                    try {
                        const clickRes = await axios.get(
                            `${process.env.REACT_APP_API_URL}/api/slg-analytics/contact-clicks`,
                            { headers: authHeaders(), params: { from: chartFrom, to: chartTo } }
                        );
                        if (clickRes.data?.success) {
                            applyContactRows(clickRes.data.data, clickRes.data.meta || null);
                        } else {
                            setAllContactClicks([]);
                            setContactMeta(null);
                            setContactError(clickRes.data?.error || 'Failed to load contact clicks');
                        }
                    } catch (clickErr) {
                        setAllContactClicks([]);
                        setContactMeta(null);
                        setContactError(
                            clickErr.response?.status === 404
                                ? 'Contact clicks API not on server yet — deploy/restart ERP backend (smbapi) with latest code.'
                                : (clickErr.response?.data?.error || clickErr.message || 'Failed to load contact clicks')
                        );
                    }
                }
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
            setContactLoading(false);
        }
    }, [chartFrom, chartTo]);

    const fetchChart = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setChartLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/slg-analytics/chart`,
                {
                    headers: authHeaders(),
                    params: { from: chartFrom, to: chartTo },
                }
            );
            if (res.data.success) setChartData(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load chart');
        } finally {
            setChartLoading(false);
        }
    }, [chartFrom, chartTo]);

    const fetchClickChart = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setClickChartLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/slg-analytics/click-chart`,
                {
                    headers: authHeaders(),
                    params: {
                        from: chartFrom,
                        to: chartTo,
                        ...(clickSearch ? { search: clickSearch } : {}),
                    },
                }
            );
            if (res.data.success) {
                setClickChart(res.data.data || { data: [], lines: [], filterSummary: '' });
            }
        } catch (err) {
            setClickChart({ data: [], lines: [], filterSummary: '' });
            if (err.response?.status === 404) {
                setError(
                    'Click rate API not on server yet — deploy/restart ERP backend (smbapi) with latest code.'
                );
            } else {
                setError(err.response?.data?.error || err.message || 'Failed to load click chart');
            }
        } finally {
            setClickChartLoading(false);
        }
    }, [chartFrom, chartTo, clickSearch]);

    const fetchSendChart = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setSendChartLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/slg-analytics/send-chart`,
                {
                    headers: authHeaders(),
                    params: {
                        from: chartFrom,
                        to: chartTo,
                        ...(sendSearch ? { search: sendSearch } : {}),
                    },
                }
            );
            if (res.data.success) {
                setSendChart(res.data.data || { data: [], lines: [], filterSummary: '' });
            }
        } catch (err) {
            setSendChart({ data: [], lines: [], filterSummary: '' });
            if (err.response?.status === 404) {
                setError(
                    'Send rate API not on server yet — deploy/restart ERP backend (smbapi) with latest code.'
                );
            } else {
                setError(err.response?.data?.error || err.message || 'Failed to load send chart');
            }
        } finally {
            setSendChartLoading(false);
        }
    }, [chartFrom, chartTo, sendSearch]);

    useEffect(() => {
        setContactClicks(filterContactRows(allContactClicks, contactSearch));
        setContactPage(1);
    }, [allContactClicks, contactSearch]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    useEffect(() => {
        if (!rangeReady) return;
        fetchChart();
        fetchClickChart();
        fetchSendChart();
    }, [rangeReady, fetchChart, fetchClickChart, fetchSendChart]);

    const summary = dashboard?.trafficSummary;
    const trafficSeries = clipAnalyticsChartData(chartData, chartFrom, chartTo);
    const clickSeries = clipAnalyticsChartData(clickChart.data, chartFrom, chartTo);
    const sendSeries = clipAnalyticsChartData(sendChart.data, chartFrom, chartTo);
    const contactTotalPages = Math.max(1, Math.ceil(contactClicks.length / CONTACT_PAGE_SIZE));
    const contactRows = contactClicks.slice(
        (contactPage - 1) * CONTACT_PAGE_SIZE,
        contactPage * CONTACT_PAGE_SIZE
    );

    const activeVisitorColumns = [
        { key: 'path', label: 'Page' },
        { key: 'views', label: 'Views' },
        { key: 'ip', label: 'IP' },
        { key: 'browser', label: 'Browser' },
        { key: 'device', label: 'Device', render: (row) => formatDeviceDisplay(row.device) },
        { key: 'country', label: 'Country' },
        {
            key: 'lastViewedAt',
            label: 'Last seen',
            render: (row) => formatSingaporeDateTime(row.lastViewedAt),
        },
    ];

    const latestVisitorColumns = [
        { key: 'path', label: 'Page' },
        { key: 'ip', label: 'IP' },
        { key: 'browser', label: 'Browser' },
        { key: 'device', label: 'Device', render: (row) => formatDeviceDisplay(row.device) },
        { key: 'country', label: 'Country' },
        {
            key: 'viewedAt',
            label: 'Viewed at',
            render: (row) => formatSingaporeDateTime(row.viewedAt),
        },
    ];

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <FiActivity className="h-6 w-6 text-blue-500" />
                        <h1 className="text-2xl font-bold">SLGWeb Analytics</h1>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <AnalyticsDateRange
                            chartFrom={chartFrom}
                            setChartFrom={setChartFrom}
                            chartTo={chartTo}
                            setChartTo={setChartTo}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                fetchDashboard();
                                fetchChart();
                                fetchClickChart();
                                fetchSendChart();
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            <FiRefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-4">
                        <button
                            type="button"
                            onClick={() => setChartTab('traffic')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                chartTab === 'traffic'
                                    ? 'border-blue-600 text-blue-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Visitors &amp; views over time
                        </button>
                        <button
                            type="button"
                            onClick={() => setChartTab('clicks')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                chartTab === 'clicks'
                                    ? 'border-blue-600 text-blue-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Click rate over time
                        </button>
                        <button
                            type="button"
                            onClick={() => setChartTab('sends')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                chartTab === 'sends'
                                    ? 'border-blue-600 text-blue-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Send rate over time
                        </button>
                    </div>

                    <div className="mb-4 space-y-2">
                        {chartTab === 'sends' ? (
                            <p className="text-xs text-gray-500">Accurate data from 31 Jul 2026</p>
                        ) : null}
                        <div className="flex flex-wrap items-end gap-4">
                            {(chartTab === 'clicks' || chartTab === 'sends') && (
                                <>
                                    <div className="relative">
                                        <label className="block text-xs text-gray-500 mb-1">
                                            Filter by god name / webID
                                        </label>
                                        <input
                                            type="text"
                                            value={chartTab === 'clicks' ? clickSearchInput : sendSearchInput}
                                            onChange={(e) => {
                                                if (chartTab === 'clicks') {
                                                    setClickSearchInput(e.target.value);
                                                } else {
                                                    setSendSearchInput(e.target.value);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    if (chartTab === 'clicks') {
                                                        setClickSearch(clickSearchInput.trim());
                                                    } else {
                                                        setSendSearch(sendSearchInput.trim());
                                                    }
                                                }
                                            }}
                                            placeholder="Search god name or webID"
                                            className="border border-gray-300 rounded-md pl-3 pr-9 py-1.5 text-sm w-56"
                                        />
                                        <FiSearch className="absolute right-3 bottom-2.5 text-gray-400 w-4 h-4 pointer-events-none" />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (chartTab === 'clicks') {
                                                setClickSearch(clickSearchInput.trim());
                                            } else {
                                                setSendSearch(sendSearchInput.trim());
                                            }
                                        }}
                                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Search
                                    </button>
                                    {(chartTab === 'clicks'
                                        ? clickSearch || clickSearchInput
                                        : sendSearch || sendSearchInput) && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (chartTab === 'clicks') {
                                                    setClickSearchInput('');
                                                    setClickSearch('');
                                                } else {
                                                    setSendSearchInput('');
                                                    setSendSearch('');
                                                }
                                            }}
                                            className="px-3 py-1.5 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        {chartTab === 'clicks' && clickSearch && clickChart.filterSummary ? (
                            <p className="w-full text-xs text-blue-700 whitespace-pre-line">
                                {clickChart.filterSummary}
                            </p>
                        ) : null}
                        {chartTab === 'sends' && sendSearch && sendChart.filterSummary ? (
                            <p className="w-full text-xs text-blue-700 whitespace-pre-line">
                                {sendChart.filterSummary}
                            </p>
                        ) : null}
                    </div>

                    {chartTab === 'traffic' ? (
                        chartLoading ? (
                            <p className="text-gray-500 py-12 text-center">Loading chart...</p>
                        ) : trafficSeries.length === 0 ? (
                            <p className="text-gray-500 py-12 text-center">No data for selected range</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart key={`traffic-${chartFrom}-${chartTo}`} data={trafficSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis {...ANALYTICS_DATE_XAXIS_PROPS} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="visitors"
                                        name="Visitors"
                                        stroke="#2563eb"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="views"
                                        name="Views"
                                        stroke="#16a34a"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )
                    ) : chartTab === 'clicks' ? (
                        clickChartLoading ? (
                            <p className="text-gray-500 py-12 text-center">Loading click chart...</p>
                        ) : !clickSeries.length || !clickChart.lines?.length ? (
                            <p className="text-gray-500 py-12 text-center">No click data for selected range</p>
                        ) : (
                            <ResponsiveContainer width="100%" height={360}>
                                <LineChart key={`clicks-${chartFrom}-${chartTo}`} data={clickSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis {...ANALYTICS_DATE_XAXIS_PROPS} />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 11 }}
                                        label={{
                                            value: 'Click count',
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: { fontSize: 11 },
                                        }}
                                    />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                                    {(clickChart.lines || []).map((line) => (
                                        <Line
                                            key={line.key}
                                            type="monotone"
                                            dataKey={line.key}
                                            name={line.name}
                                            stroke={line.color}
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        )
                    ) : sendChartLoading ? (
                        <p className="text-gray-500 py-12 text-center">Loading send chart...</p>
                    ) : !sendSeries.length || !sendChart.lines?.length ? (
                        <p className="text-gray-500 py-12 text-center">No send data for selected range</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={360}>
                            <LineChart key={`sends-${chartFrom}-${chartTo}`} data={sendSeries}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis {...ANALYTICS_DATE_XAXIS_PROPS} />
                                <YAxis
                                    allowDecimals={false}
                                    tick={{ fontSize: 11 }}
                                    label={{
                                        value: 'Send count',
                                        angle: -90,
                                        position: 'insideLeft',
                                        style: { fontSize: 11 },
                                    }}
                                />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: '11px' }} />
                                {(sendChart.lines || []).map((line) => (
                                    <Line
                                        key={line.key}
                                        type="monotone"
                                        dataKey={line.key}
                                        name={line.name}
                                        stroke={line.color}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {loading ? (
                    <p className="text-gray-600">Loading dashboard...</p>
                ) : dashboard ? (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                                <p className="text-sm font-medium text-blue-800">Currently online</p>
                                <p className="text-3xl font-bold text-blue-700 mt-1">
                                    {dashboard.onlineNow ?? 0}
                                </p>
                                <p className="text-xs text-blue-600 mt-1">Last 15 minutes</p>
                            </div>
                            <StatCard label="Today" {...summary?.today} />
                            <StatCard label="Yesterday" {...summary?.yesterday} />
                            <StatCard label="Last 7 days" {...summary?.last7Days} />
                            <StatCard label="Last 30 days" {...summary?.last30Days} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            <PaginatedDataTable
                                title="Referrals from search engines"
                                columns={[
                                    { key: 'searchEngine', label: 'Search engine' },
                                    { key: 'visits', label: 'Visits' },
                                ]}
                                rows={dashboard.searchReferrals}
                            />
                            <PaginatedDataTable
                                title="Top pages"
                                columns={[
                                    { key: 'path', label: 'Page' },
                                    { key: 'views', label: 'Views' },
                                    { key: 'uniqueVisitors', label: 'Visitors' },
                                ]}
                                rows={dashboard.topPages}
                            />
                            <PaginatedDataTable
                                title="Traffic sources — UTM"
                                columns={[
                                    { key: 'utmSource', label: 'Source' },
                                    { key: 'utmMedium', label: 'Medium' },
                                    { key: 'utmCampaign', label: 'Campaign' },
                                    { key: 'visits', label: 'Visits' },
                                ]}
                                rows={dashboard.utmSources}
                            />
                            <PaginatedDataTable
                                title="Top referring sites"
                                columns={[
                                    { key: 'referrerDomain', label: 'Site' },
                                    { key: 'visits', label: 'Visits' },
                                    { key: 'uniqueVisitors', label: 'Visitors' },
                                ]}
                                rows={dashboard.topReferrers}
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <PaginatedDataTable
                                title="Active visitors"
                                columns={activeVisitorColumns}
                                rows={dashboard.activeVisitors}
                            />
                            <PaginatedDataTable
                                title="Latest visitors"
                                columns={latestVisitorColumns}
                                rows={dashboard.latestVisitors}
                            />
                        </div>
                    </>
                ) : null}

                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-base font-semibold text-gray-800">
                            Member contact clicks
                            {contactMeta?.rawCount != null && (
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                    ({contactMeta.rawCount} raw click{contactMeta.rawCount === 1 ? '' : 's'}
                                    {contactMeta.collection ? ` · ${contactMeta.collection}` : ''})
                                </span>
                            )}
                        </h2>
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={contactSearchInput}
                                    onChange={(e) => setContactSearchInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            setContactSearch(contactSearchInput.trim());
                                        }
                                    }}
                                    placeholder="Search god name or webID"
                                    className="border border-gray-300 rounded-md pl-3 pr-9 py-1.5 text-sm w-56"
                                />
                                <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            </div>
                            <button
                                type="button"
                                onClick={() => setContactSearch(contactSearchInput.trim())}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Search
                            </button>
                            {(contactSearch || contactSearchInput) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setContactSearchInput('');
                                        setContactSearch('');
                                    }}
                                    className="px-3 py-1.5 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        Name - WebID
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        WhatsApp
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        Telegram
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        Phone
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        WeChat
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        Total
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        Last Click
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        Last Web Review
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                        Total Review
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {contactLoading ? (
                                    <tr>
                                        <td colSpan={9} className="px-3 py-6 text-center text-gray-500 text-sm">
                                            Loading contact clicks...
                                        </td>
                                    </tr>
                                ) : contactError ? (
                                    <tr>
                                        <td colSpan={9} className="px-3 py-6 text-center text-red-600 text-sm">
                                            {contactError}
                                        </td>
                                    </tr>
                                ) : !contactRows.length ? (
                                    <tr>
                                        <td colSpan={9} className="px-3 py-6 text-center text-gray-500 text-sm">
                                            {contactMeta?.rawCount > 0
                                                ? `Found ${contactMeta.rawCount} raw clicks but none matched this view (check webID / search).`
                                                : 'No contact clicks yet'}
                                        </td>
                                    </tr>
                                ) : (
                                    contactRows.map((row) => (
                                        <tr key={row.webID} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                                                {`${row.godName || 'Unknown'} - ${row.webID || '—'}`}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-gray-800">{row.whatsapp || 0}</td>
                                            <td className="px-3 py-2 text-sm text-gray-800">{row.telegram || 0}</td>
                                            <td className="px-3 py-2 text-sm text-gray-800">{row.phone || 0}</td>
                                            <td className="px-3 py-2 text-sm text-gray-800">{row.wechat || 0}</td>
                                            <td className="px-3 py-2 text-sm font-semibold text-gray-900">{row.total || 0}</td>
                                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                                                {row.lastClick ? formatSingaporeDateTime(row.lastClick) : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-gray-800 whitespace-nowrap">
                                                {row.lastWebReview
                                                    ? formatSingaporeDateTime(row.lastWebReview)
                                                    : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-gray-800">
                                                {row.totalReview || 0}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {!contactLoading && contactClicks.length > 0 && (
                        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 text-sm">
                            <span className="text-gray-500">
                                {contactClicks.length} member{contactClicks.length === 1 ? '' : 's'}
                                {' · '}
                                Page {contactPage} of {contactTotalPages}
                            </span>
                            <div className="flex gap-1">
                                <button
                                    type="button"
                                    onClick={() => setContactPage((p) => Math.max(1, p - 1))}
                                    disabled={contactPage === 1}
                                    className={`p-1 rounded ${contactPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                                    aria-label="Previous page"
                                >
                                    <FiChevronLeft />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setContactPage((p) => Math.min(contactTotalPages, p + 1))}
                                    disabled={contactPage === contactTotalPages}
                                    className={`p-1 rounded ${contactPage === contactTotalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                                    aria-label="Next page"
                                >
                                    <FiChevronRight />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default SLGAnalyticsPage;
